// @ts-check
import { ModuleProcessMappings } from '@erpagroapp/common';
import {
  ConfigurationParameterReturnType,
  ConfigurationParameterSource,
} from '../enums';
import { defineConfig, defineDefaultProps } from '../helpers';

const PALLETIZED_ID = ModuleProcessMappings.ProductionControl.Submodules.Palletized.Processes.Palletized.Id;
const MULTIPLE_PALLETIZED_ID = ModuleProcessMappings.ProductionControl.Submodules.Palletized.Processes.MultiplePalletized.Id;
const PICKING_ID = ModuleProcessMappings.ProductionControl.Submodules.Palletized.Processes.Picking.Id;
const MANAGE_PRODUCTION_LINES_ID = ModuleProcessMappings.ProductionControl.Processes.ManageProductionLines.Id;

const defaultProps = defineDefaultProps(/** @type {const} */ ({
  loadingRules: {
    requiredModuleIds: [
      ModuleProcessMappings.ProductionControl.Id,
    ],
  },
}));

export function traceability() {
  return defineConfig({
    PermitirCrearOrdenYParteDeProduccionPorPedidoEnMovilidad: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          MANAGE_PRODUCTION_LINES_ID,
        ],
      },
    },
    BloquearFinPaletSinDatosOrigen: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    UsoLotesMovilidad: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      useCaseCode: 'TRAUT900',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
          MULTIPLE_PALLETIZED_ID,
          ModuleProcessMappings.ProductionControl.Processes.Dumping.Id,
        ],
      },
    },
    RestaurarTipoEnvasadoPaletizadoMultiple: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          MULTIPLE_PALLETIZED_ID,
        ],
      },
    },
    NumeroTrabajadoresManualLineasProduccionLineaPedido: {
      source: ConfigurationParameterSource.ERP,
      component: 'Produccion',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          ModuleProcessMappings.ProductionControl.Processes.ProductionPlanningStatus.Id,
        ],
      },
    },
    ActualizarConfeccionPrevistaFamiliaGenericaPedido: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    LecturaContinuaUbicacionesAPP: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      useCaseCode: 'TRAUT900',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          ModuleProcessMappings.ProductionControl.Processes.Locations.Id,
        ],
      },
    },
    UbicacionDefectoEntrada: {
      source: ConfigurationParameterSource.ERP,
      component: 'Ventas',
      entityModel: 'Almacen.UbicacionJSON',
      searchField: 'Id',
      returnType: ConfigurationParameterReturnType.OBJECT,
      defaultValue: null,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PICKING_ID,
        ],
      },
    },
    ComprobarConfeccionCajaEnMovilidad: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      info: 'Comprueba que la confección de la línea está incluida en el código de barras de la caja leída',
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    EnvasadoPorDefectoMarca: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      useCaseCode: 'TRAUT900',
      returnType: ConfigurationParameterReturnType.STRING,
      defaultValue: null,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    EnvasadoPorDefectoPaletRecoleccion: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      useCaseCode: 'TRAUT900',
      returnType: ConfigurationParameterReturnType.STRING,
      defaultValue: null,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    MostrarTurnosPresencia: {
      source: ConfigurationParameterSource.WebService,
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        requiredModuleIds: [
          ModuleProcessMappings.ActivityControl.Id,
        ],
        requiredProcessIds: [
          ModuleProcessMappings.ActivityControl.Processes.ProductiveActivityLine.Id,
        ],
      },
    },
    trabajarConNumerosDeSerie: {
      source: ConfigurationParameterSource.WebService,
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      ...defaultProps,
    },
    mostrarDatosAuxiliaresLineaPedidoVenta: {
      source: ConfigurationParameterSource.WebService,
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    DatosComplementariosAObtener: {
      source: ConfigurationParameterSource.WebService,
      returnType: ConfigurationParameterReturnType.STRING,
      defaultValue: null,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          MANAGE_PRODUCTION_LINES_ID,
          PALLETIZED_ID,
        ],
      },
    },
    NoMostrarEnvasesConfeccionadosUIC: {
      source: ConfigurationParameterSource.WebService,
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      info: 'Controla la visibilidad del texto "Envases confeccionados" en la opción de menú "Paletizado".',
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
    AlertaSonoraVibracionLecturaCodigoBarras: {
      source: ConfigurationParameterSource.ERP,
      component: 'TrazabilidadAutomatizada',
      useCaseCode: 'TRAUT900',
      returnType: ConfigurationParameterReturnType.BOOLEAN,
      defaultValue: false,
      loadingRules: {
        ...defaultProps.loadingRules,
        requiredProcessIds: [
          PALLETIZED_ID,
        ],
      },
    },
  });
}
